/**
 * Who's behind a number: the people (and their accounts) a count is made
 * of. Used by the affiliate leaderboard and Social → Results. Each row opens
 * the person (or their account when there's no contact).
 */
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Drawer } from "@/components/ui";
import { num } from "@/features/command/ui";

export interface DrawerPerson {
  account_id: string;
  contact_id: string | null;
  name: string;
  /** Small grey line under the name ("signed up 3d ago"). */
  sub?: ReactNode;
  /** Right-hand value ("$89", "Paying"). */
  detail?: ReactNode;
}

export interface PeopleDrawerState {
  title: string;
  /** One line above the list, e.g. "Signed up in the last 30 days through Jana's link". */
  hint?: string;
  people: DrawerPerson[];
}

export function PeopleDrawer({ state, onClose }: { state: PeopleDrawerState | null; onClose: () => void }) {
  const { pathname, search } = useLocation();
  const from = { from: pathname + search };
  const people = state?.people ?? [];
  return (
    <Drawer open={!!state} onClose={onClose} title={state?.title ?? ""} width="md">
      {people.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-zinc-500">Nobody here yet.</p>
      ) : (
        <div className="px-4 py-3">
          <p className="mb-2 px-1 text-xs text-zinc-500">
            {num(people.length)} {people.length === 1 ? "person" : "people"}{state?.hint ? `. ${state.hint}` : ""}
          </p>
          <ul className="divide-y divide-zinc-800/60">
            {people.map((p) => (
              <li key={p.account_id}>
                <Link
                  to={p.contact_id ? `/contacts/${p.contact_id}` : `/accounts/${p.account_id}`}
                  state={from}
                  onClick={onClose}
                  className="group flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-zinc-900/60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-zinc-100 group-hover:text-indigo-200">{p.name}</span>
                    {p.sub && <span className="mt-0.5 block truncate text-[11px] text-zinc-500">{p.sub}</span>}
                  </span>
                  {p.detail != null && <span className="shrink-0 text-xs font-medium tabular-nums text-zinc-300">{p.detail}</span>}
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-zinc-600" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
}
